import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { keys } from '@/lib/query/keys';
import { useAuth } from '@/lib/auth/useAuth';
import { cn } from '@/lib/ui/cn';
import { attentionItems } from '@/features/dashboard/AttentionPanel';
import type { DashboardSummary } from '@/lib/api/types';

/**
 * What is waiting on someone, in one line above the website's pages: unseen visitor
 * messages, drafts that cannot be published, emails that never left. The same items as the
 * overview's panel (one `GET /admin/dashboard`, shared with the sidebar's inbox counts), but
 * brief, because the pages below are what this screen is for. The full picture is one link
 * away on the overview.
 */
export function AttentionStrip() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const summary = useQuery({
    queryKey: keys.dashboard(),
    queryFn: () => api.get<DashboardSummary>('/admin/dashboard'),
    staleTime: 60_000,
  });
  if (!summary.data) return null;
  const items = attentionItems(summary.data, { can, salesOnly: false, t });

  return (
    <section
      aria-labelledby="attention-strip"
      className="mb-lg flex flex-wrap items-baseline gap-x-md gap-y-xs rounded-md bg-canvas-paper px-md py-sm"
    >
      <h2 id="attention-strip" className="text-small font-medium text-ink-deep">
        {t('attention.title')}
      </h2>
      {items.length === 0 ? (
        <p className="text-small text-ink-mid">{t('website.home.allClear')}</p>
      ) : (
        <ul className="flex flex-wrap items-baseline gap-x-md gap-y-xs">
          {items.map((item) => (
            <li key={item.id} className="flex items-center gap-xs text-small text-ink-graphite">
              <span
                aria-hidden
                className={cn(
                  'size-[8px] shrink-0 rounded-pill',
                  item.tone === 'amber' ? 'bg-signal-amber' : 'bg-accent-blue',
                )}
              />
              {item.to ? (
                <Link
                  // The outbox is an anchor on the overview, not a screen of its own.
                  to={item.anchor ? '/overview' : item.to}
                  className="rounded-xs text-ink-deep underline decoration-hairline-strong underline-offset-4 transition-colors duration-almanac hover:text-accent-blue hover:decoration-accent-blue focus-visible:shadow-focus"
                >
                  {item.label} <span className="tnum font-mono">{item.count}</span>
                </Link>
              ) : (
                <span>
                  {item.label} <span className="tnum font-mono">{item.count}</span>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      <Link
        to="/overview"
        className="ms-auto rounded-xs text-small text-accent-blue underline-offset-4 hover:text-ink-deep hover:underline focus-visible:shadow-focus"
      >
        {t('website.home.overview')}
      </Link>
    </section>
  );
}
